import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { alpha } from '@mui/material/styles';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton,
  Paper, Stack, Typography,
} from '@mui/material';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

import LabeledField from '../../../shared/components/LabeledField';
import AppDialog from '../../../shared/components/AppDialog';
import { applyApiFieldErrors } from '../../../shared/api';
import ProcessingCloseWarningDialog from '../../../shared/components/ProcessingCloseWarningDialog';
import useProcessingCloseGuard from '../../../shared/hooks/useProcessingCloseGuard';
import {
  groupRecalculationCategories,
  toRecalculationForm,
  toRecalculationPayload,
} from '../model/recalculationForm';

export default function RecalculationDialog({
  open,
  year,
  categories,
  running,
  monitoringError,
  hasAnyCalculatedResult,
  onClose,
  onSubmit,
  onRetryStatus,
}) {
  const { t } = useTranslation();
  const [reviewing, setReviewing] = useState(false);
  const {
    requestClose, isWarningOpen, stay, closeAnyway,
  } = useProcessingCloseGuard({ isProcessing: running, onClose });
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: toRecalculationForm(categories),
  });

  useEffect(() => {
    if (open) {
      reset(toRecalculationForm(categories));
      setReviewing(false);
    }
  }, [categories, open, reset]);

  const selectedIds = watch('category_ids');
  const availableCategories = useMemo(
    () => categories.filter((item) => item.selectable !== false),
    [categories],
  );
  const groupedCategories = useMemo(
    () => groupRecalculationCategories(categories),
    [categories],
  );
  const selectedRows = useMemo(
    () => categories.filter((item) => (selectedIds || []).includes(item.id)),
    [categories, selectedIds],
  );
  const review = handleSubmit(() => setReviewing(true));
  const submit = handleSubmit(async (values) => {
    clearErrors();
    try {
      await onSubmit(toRecalculationPayload(values));
    } catch (error) {
      const handled = applyApiFieldErrors(error, setError, t, {
        mapField: (field) => {
          const name = String(field || '').split('.').pop();
          if (['targets', 'category_ids'].includes(name)) return 'category_ids';
          return null;
        },
      });
      if (!handled) throw error;
      setReviewing(false);
    }
  });
  return (
    <>
      <AppDialog open={open} onClose={requestClose} maxWidth="md" fullWidth sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}>
        <DialogTitle sx={{ pr: 7, py: 2.25 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={(theme) => ({
                alignItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                color: 'primary.main',
                display: 'flex',
                height: 40,
                justifyContent: 'center',
                mt: 0.25,
                width: 40,
              })}
            >
              <CalculateOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="div" fontWeight={750}>
                {t(hasAnyCalculatedResult
                  ? 'dataInput.recalculation.title'
                  : 'dataInput.recalculation.titleFirstTime')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {t('dataInput.recalculation.subtitle')}
              </Typography>
              <Chip
                size="small"
                label={t('dataInput.recalculation.period', { year })}
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.dark',
                  fontWeight: 700,
                  mt: 1,
                })}
              />
            </Box>
          </Stack>
          <IconButton
            onClick={requestClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: '#f7f9fc', px: { xs: 2, sm: 3 } }}>
          {running && monitoringError && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" onClick={onRetryStatus}>
                  {t('dataInput.recalculation.retryStatus')}
                </Button>
              )}
              sx={{ mb: 2 }}
            >
              {t('dataInput.recalculation.statusUnavailable')}
            </Alert>
          )}
          {errors.root?.server?.message && (
            <Alert severity="error" sx={{ mb: 2 }}>{errors.root.server.message}</Alert>
          )}
          {!reviewing ? (
            <Stack spacing={2.5}>
              <Controller
                name="category_ids"
                control={control}
                rules={{ validate: (value) => value.length > 0 || t('dataInput.recalculation.categoriesRequired') }}
                render={({ field }) => {
                  const allSelected = availableCategories.length > 0
                    && field.value.length === availableCategories.length;
                  return (
                    <LabeledField
                      label={t('dataInput.recalculation.categories')}
                      required
                      error={errors.category_ids?.message}
                    >
                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            alignItems: 'center',
                            bgcolor: 'common.white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 0.75,
                          }}
                        >
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={allSelected}
                                indeterminate={field.value.length > 0 && !allSelected}
                                onChange={(event) => field.onChange(
                                  event.target.checked
                                    ? availableCategories.map((item) => item.id)
                                    : [],
                                )}
                                disabled={availableCategories.length === 0}
                              />
                            )}
                            label={(
                              <Box>
                                <Typography variant="body2" fontWeight={700}>
                                  {t('dataInput.recalculation.selectAll')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('dataInput.recalculation.availableSummary')}
                                </Typography>
                              </Box>
                            )}
                            sx={{ m: 0, minWidth: 0 }}
                          />
                        </Box>
                        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5 }}>
                          <Stack spacing={2}>
                            {groupedCategories.map((group) => (
                              <Box key={group.id}>
                                <Typography
                                  variant="overline"
                                  sx={{ color: 'text.secondary', fontWeight: 800, lineHeight: 1.8 }}
                                >
                                  {t(`dataInput.recalculation.scopeGroup.${group.id}`)}
                                </Typography>
                                <Stack spacing={1}>
                                  {group.items.map((item) => {
                                    const selected = field.value.includes(item.id);
                                    return (
                                      <Paper
                                        key={item.id}
                                        component="label"
                                        variant="outlined"
                                        sx={{
                                          alignItems: 'flex-start',
                                          bgcolor: item.selectable ? 'common.white' : 'action.hover',
                                          borderColor: selected ? 'primary.main' : 'divider',
                                          cursor: item.selectable ? 'pointer' : 'not-allowed',
                                          display: 'flex',
                                          px: 1.25,
                                          py: 1.25,
                                          transition: 'border-color 120ms ease, box-shadow 120ms ease',
                                          ...(selected && { boxShadow: '0 0 0 1px rgba(0, 120, 212, 0.12)' }),
                                        }}
                                      >
                                        <Checkbox
                                          checked={selected}
                                          disabled={!item.selectable}
                                          onChange={(event) => field.onChange(
                                            event.target.checked
                                              ? [...field.value, item.id]
                                              : field.value.filter((id) => id !== item.id),
                                          )}
                                          sx={{ ml: -0.5, mr: 0.75, mt: -0.75 }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            justifyContent="space-between"
                                          >
                                            <Box sx={{ minWidth: 0 }}>
                                              <Typography
                                                variant="body2"
                                                fontWeight={750}
                                                color={item.selectable ? 'text.primary' : 'text.secondary'}
                                              >
                                                {t(item.labelKey)}
                                              </Typography>
                                              {item.id === 'scope3_af' && (
                                                <Typography variant="caption" color="text.secondary">
                                                  {t('dataInput.recalculation.scope3AfCoverage')}
                                                </Typography>
                                              )}
                                            </Box>
                                            <Chip
                                              size="small"
                                              icon={item.selectable
                                                ? <CheckCircleRoundedIcon />
                                                : <ErrorOutlineRoundedIcon />}
                                              color={item.selectable ? 'success' : 'default'}
                                              variant={item.selectable ? 'filled' : 'outlined'}
                                              label={t(item.selectable
                                                ? (item.readiness === 'AVAILABLE'
                                                  ? 'dataInput.recalculation.available'
                                                  : 'dataInput.recalculation.ready')
                                                : 'dataInput.recalculation.notReady')}
                                              sx={{ flexShrink: 0, fontWeight: 700 }}
                                            />
                                          </Stack>

                                          {!item.selectable ? (
                                            <Stack
                                              spacing={0.75}
                                              sx={{ color: 'warning.dark', mt: 0.75 }}
                                            >
                                              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                <ErrorOutlineRoundedIcon sx={{ fontSize: 16, mt: '1px' }} />
                                                <Typography variant="caption" fontWeight={650}>
                                                  {t(`dataInput.recalculation.readiness.${item.readiness}`, {
                                                    defaultValue: t('dataInput.recalculation.readiness.TARGET_NOT_READY'),
                                                  })}
                                                </Typography>
                                              </Stack>
                                            </Stack>
                                          ) : (
                                            (item.warnings || []).map((warning) => (
                                              <Typography
                                                key={warning}
                                                variant="caption"
                                                color="warning.dark"
                                                sx={{ display: 'block', fontWeight: 650, mt: 0.5 }}
                                              >
                                                {t(`dataInput.recalculation.warning.${warning}`, {
                                                  defaultValue: t('dataInput.recalculation.warning.UNKNOWN'),
                                                })}
                                              </Typography>
                                            ))
                                          )}
                                        </Box>
                                      </Paper>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Paper>
                    </LabeledField>
                  );
                }}
              />

            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette.primary.main, 0.055),
                  borderColor: alpha(theme.palette.primary.main, 0.16),
                  p: 2,
                })}
              >
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {t('dataInput.recalculation.confirmTitle')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 750 }}>
                  {t('dataInput.recalculation.confirmSummary')}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={750} sx={{ mb: 1 }}>
                  {t('dataInput.recalculation.selectedCategories')}
                </Typography>
                <Stack spacing={0.75}>
                  {selectedRows.map((item) => (
                    <Typography key={item.id} variant="body2">
                      {t(item.labelKey)}
                    </Typography>
                  ))}
                </Stack>
              </Paper>

              <Alert severity="info" variant="outlined">
                {t('dataInput.recalculation.reviewWarning', { year })}
              </Alert>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            gap: { xs: 0.5, sm: 1 },
            px: 3,
            py: 2,
            '& > :not(style) ~ :not(style)': { ml: 0 },
            '& .MuiButton-root': { minWidth: 0, whiteSpace: 'nowrap' },
          }}
        >
          <Button onClick={requestClose}>{t('common.cancel')}</Button>
          {reviewing && (
            <Button onClick={() => setReviewing(false)} disabled={running}>
              {t('dataInput.recalculation.back')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={reviewing ? submit : review}
            disabled={running || availableCategories.length === 0 || selectedRows.length === 0}
            startIcon={running
              ? <CircularProgress color="inherit" size={15} />
              : <CalculateOutlinedIcon />}
          >
            {running
              ? t('dataInput.recalculation.running')
              : t(reviewing ? 'dataInput.recalculation.confirm' : 'dataInput.recalculation.review')}
          </Button>
        </DialogActions>
      </AppDialog>
      <ProcessingCloseWarningDialog
        open={isWarningOpen}
        onStay={stay}
        onCloseAnyway={closeAnyway}
      />
    </>
  );
}
